export interface MatchProfile {
  id: string;
  name: string | null;
  gender: string | null;
  age: number | null;
  height: number | null;
  weight: number | null;
  education: string | null;
  marital_status: string | null;
  children_status: string | null;
  residence: string | null;
  hometown: string | null;
  occupation: string | null;
  income: string | null;
  house_status: string | null;
  car_status: string | null;
  phone: string | null;
  wechat: string | null;
  hobbies: string | null;
  self_intro: string | null;
  partner_age_min: number | null;
  partner_age_max: number | null;
  partner_requirements: string | null;
  photo_url: string | null;
  status: string | null;
  created_at: string;
}

export const GENDER_OPTIONS = ["男", "女"];
export const MARITAL_OPTIONS = ["未婚", "离异", "丧偶"];
export const CHILDREN_OPTIONS = ["无子女", "子女未成年同住", "子女已独立", "子女已成家"];
export const HOUSE_OPTIONS = ["有房无贷款", "有房有贷款", "与子女同住", "租房居住", "暂未购房"];
export const CAR_OPTIONS = ["有车", "无车"];
export const INCOME_OPTIONS = [
  "3000元以下",
  "3000-5000元",
  "5000-8000元",
  "8000-12000元",
  "12000元以上",
];

export interface StatusMeta {
  value: string;
  label: string;
  badge: string;
  dot: string;
}

export const STATUS_OPTIONS: StatusMeta[] = [
  { value: "pending", label: "待处理", badge: "bg-accent-100 text-accent-800", dot: "bg-accent-500" },
  { value: "contacted", label: "已联系", badge: "bg-secondary-100 text-secondary-800", dot: "bg-secondary-500" },
  { value: "matched", label: "已匹配", badge: "bg-primary-100 text-primary-700", dot: "bg-primary-500" },
  { value: "closed", label: "已下线", badge: "bg-background-200 text-foreground-600", dot: "bg-foreground-400" },
];

export function getStatusMeta(status?: string | null): StatusMeta {
  return STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];
}

export interface DetailField {
  key: keyof MatchProfile;
  label: string;
  suffix?: string;
  full?: boolean;
}

export interface DetailGroup {
  title: string;
  icon: string;
  fields: DetailField[];
}

export const DETAIL_GROUPS: DetailGroup[] = [
  {
    title: "基本信息",
    icon: "ri-user-3-line",
    fields: [
      { key: "name", label: "姓名" },
      { key: "gender", label: "性别" },
      { key: "age", label: "年龄", suffix: " 岁" },
      { key: "height", label: "身高", suffix: " cm" },
      { key: "weight", label: "体重", suffix: " kg" },
      { key: "education", label: "学历" },
    ],
  },
  {
    title: "生活状况",
    icon: "ri-home-heart-line",
    fields: [
      { key: "marital_status", label: "婚姻状况" },
      { key: "children_status", label: "子女情况" },
      { key: "residence", label: "现居城市" },
      { key: "hometown", label: "籍贯" },
      { key: "occupation", label: "职业" },
      { key: "income", label: "月收入" },
      { key: "house_status", label: "住房情况" },
      { key: "car_status", label: "车辆情况" },
    ],
  },
  {
    title: "自我与择偶",
    icon: "ri-heart-2-line",
    fields: [
      { key: "hobbies", label: "兴趣爱好", full: true },
      { key: "self_intro", label: "自我介绍", full: true },
      { key: "partner_age_min", label: "期望对方年龄（起）", suffix: " 岁" },
      { key: "partner_age_max", label: "期望对方年龄（止）", suffix: " 岁" },
      { key: "partner_requirements", label: "择偶要求", full: true },
    ],
  },
];

export const CONTACT_FIELDS: DetailField[] = [
  { key: "phone", label: "手机号" },
  { key: "wechat", label: "微信号" },
];

export function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

export function displayValue(value: unknown, suffix?: string): string {
  if (value === null || value === undefined || value === "") return "—";
  return `${value}${suffix || ""}`;
}

export type StaffRoleKey = "admin" | "sales";

export interface RoleMeta {
  label: string;
  badge: string;
  icon: string;
  desc: string;
}

export const ROLE_META: Record<StaffRoleKey, RoleMeta> = {
  admin: {
    label: "管理员",
    badge: "bg-primary-100 text-primary-700",
    icon: "ri-shield-star-line",
    desc: "拥有全部权限：查看完整手机号、导入资料、管理员工账号",
  },
  sales: {
    label: "销售",
    badge: "bg-secondary-100 text-secondary-800",
    icon: "ri-user-star-line",
    desc: "可查看会员资料与跟进状态，手机号自动脱敏显示",
  },
};

export interface StaffMember {
  user_id: string;
  username: string | null;
  email: string | null;
  display_name: string | null;
  role: StaffRoleKey;
  created_at: string;
}